import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { getErrorMessage } from "../utils/errorHandler";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { 
  Image, Sparkles, Palette, Check, Loader2, Wand2, Upload, Layers, Video, Link, Play
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRADIENT_PRESETS = [
  { id: "midnight", name: "Midnight", value: "linear-gradient(135deg, #1e1b4b 0%, #000000 100%)" },
  { id: "sunset", name: "Sunset", value: "linear-gradient(135deg, #f59e0b 0%, #dc2626 100%)" },
  { id: "ocean", name: "Ocean", value: "linear-gradient(135deg, #0ea5e9 0%, #1e3a8a 100%)" },
  { id: "forest", name: "Forest", value: "linear-gradient(135deg, #10b981 0%, #064e3b 100%)" },
  { id: "purple", name: "Purple Haze", value: "linear-gradient(135deg, #8b5cf6 0%, #4c1d95 100%)" },
  { id: "rose", name: "Rose", value: "linear-gradient(135deg, #f43f5e 0%, #881337 100%)" },
  { id: "gold", name: "Golden Hour", value: "linear-gradient(135deg, #fbbf24 0%, #b45309 100%)" },
  { id: "slate", name: "Slate", value: "linear-gradient(135deg, #475569 0%, #0f172a 100%)" },
];

const AI_STYLE_OPTIONS = [
  { value: "cinematic", label: "Cinematic", desc: "Dramatic, high contrast" },
  { value: "abstract", label: "Abstract", desc: "Flowing shapes, modern" },
  { value: "nature", label: "Nature", desc: "Landscapes, serene" },
  { value: "worship", label: "Worship", desc: "Spiritual, ethereal" },
  { value: "minimalist", label: "Minimalist", desc: "Clean, simple" },
];

// GIF presets - looping animated backgrounds
const GIF_PRESETS = [
  { id: "stars", name: "Starry Night", url: "https://media.giphy.com/media/3o7TKSjRrfIPjeiVyM/giphy.gif", category: "space" },
  { id: "aurora", name: "Aurora", url: "https://media.giphy.com/media/l0HlPystfePnAI3G8/giphy.gif", category: "nature" },
  { id: "clouds", name: "Clouds", url: "https://media.giphy.com/media/xT9DPIBYf0pAviBLzO/giphy.gif", category: "nature" },
  { id: "water", name: "Water Ripples", url: "https://media.giphy.com/media/3o7TKz2eMXx7dn95FS/giphy.gif", category: "nature" },
  { id: "fire", name: "Fire", url: "https://media.giphy.com/media/3o7TKSjRrfIPjeiVyM/giphy.gif", category: "abstract" },
  { id: "particles", name: "Particles", url: "https://media.giphy.com/media/xUOxf5Ht2HNFgaHEI0/giphy.gif", category: "abstract" },
  { id: "gradient_anim", name: "Animated Gradient", url: "https://media.giphy.com/media/26BROrSHlmyzzHf3i/giphy.gif", category: "abstract" },
  { id: "bokeh", name: "Bokeh Lights", url: "https://media.giphy.com/media/l0HlAgJTVaAPHEGdy/giphy.gif", category: "abstract" },
  { id: "rain", name: "Rain", url: "https://media.giphy.com/media/3o7TKSjRrfIPjeiVyM/giphy.gif", category: "nature" },
  { id: "cross_light", name: "Cross Light", url: "https://media.giphy.com/media/l378rrt5tAawaCQ9i/giphy.gif", category: "worship" },
  { id: "dove", name: "Dove Flying", url: "https://media.giphy.com/media/3o7TKz2eMXx7dn95FS/giphy.gif", category: "worship" },
  { id: "candle", name: "Candle Flame", url: "https://media.giphy.com/media/xUOxf5Ht2HNFgaHEI0/giphy.gif", category: "worship" },
];

const GIF_STYLE_OPTIONS = [
  { value: "worship", label: "Worship", desc: "Cross, light rays, spiritual" },
  { value: "nature", label: "Nature", desc: "Clouds, water, forests" },
  { value: "abstract", label: "Abstract", desc: "Particles, gradients, shapes" },
  { value: "space", label: "Space", desc: "Stars, galaxies, cosmos" },
  { value: "celebration", label: "Celebration", desc: "Confetti, sparkles, joy" },
];

export const WallpaperSelector = ({ 
  open, 
  onOpenChange, 
  currentBackground,
  currentBackgroundType,
  currentBackgroundVideo,
  onSelect,
  onApplyToAll,
  projectId
}) => {
  const [presets, setPresets] = useState([]);
  const [videoPresets, setVideoPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiStyle, setAiStyle] = useState("worship");
  const [selectedTab, setSelectedTab] = useState("presets");
  const [generatedImage, setGeneratedImage] = useState(null);
  const [applyToAll, setApplyToAll] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [gifPrompt, setGifPrompt] = useState("");
  const [gifStyle, setGifStyle] = useState("worship");
  const [generatingGif, setGeneratingGif] = useState(false);
  const [generatedGif, setGeneratedGif] = useState(null);
  const [gifUrl, setGifUrl] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      fetchPresets();
      fetchVideoPresets();
    }
  }, [open]);

  const fetchPresets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/wallpapers/presets`);
      setPresets(response.data);
    } catch (error) {
      console.error("Failed to load presets");
    } finally {
      setLoading(false);
    }
  };

  const fetchVideoPresets = async () => {
    try {
      const response = await axios.get(`${API}/videos/presets`);
      setVideoPresets(response.data);
    } catch (error) {
      console.error("Failed to load video presets");
    }
  };

  const handleGenerateWallpaper = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Please enter a prompt");
      return;
    }

    setGenerating(true);
    setGeneratedImage(null);
    
    try {
      const response = await axios.post(`${API}/wallpapers/generate`, {
        prompt: aiPrompt,
        style: aiStyle
      }, { timeout: 120000 }); // 2 min timeout for image generation

      setGeneratedImage(response.data.image_base64);
      toast.success("Wallpaper generated!");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to generate wallpaper"));
    } finally {
      setGenerating(false);
    }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a JPG, PNG, WebP or GIF image");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large. Max 5MB allowed.");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-background`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const bgData = {
        background_type: "image",
        background_image: response.data.image_base64,
        background_color: "#000000"
      };

      if (applyToAll && onApplyToAll) {
        await onApplyToAll(bgData);
      } else {
        onSelect(bgData);
      }
      
      toast.success("Background uploaded!");
      onOpenChange(false);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to upload image"));
    } finally {
      setUploading(false);
    }
  };

  const applyBackground = async (bgData) => {
    if (applyToAll && onApplyToAll) {
      await onApplyToAll(bgData);
    } else {
      onSelect(bgData);
    }
    onOpenChange(false);
  };

  const handleSelectPreset = (preset) => {
    applyBackground({
      background_type: "image",
      background_image: preset.url,
      background_color: "#000000"
    });
  };

  const handleSelectGradient = (gradient) => {
    applyBackground({
      background_type: "gradient",
      background_image: gradient.value,
      background_color: "#000000"
    });
  };

  const handleSelectGenerated = () => {
    if (generatedImage) {
      applyBackground({
        background_type: "image",
        background_image: generatedImage,
        background_color: "#000000"
      });
    }
  };

  const handleSelectColor = (color) => {
    applyBackground({
      background_type: "color",
      background_image: null,
      background_video: null,
      background_color: color
    });
  };

  const handleSelectVideo = (videoUrl) => {
    applyBackground({
      background_type: "video",
      background_image: null,
      background_video: videoUrl,
      background_color: "#000000"
    });
  };

  const handleSelectGif = (gifUrl) => {
    applyBackground({
      background_type: "gif",
      background_image: gifUrl,
      background_video: null,
      background_color: "#000000"
    });
  };

  const handleSelectCustomGif = () => {
    if (!gifUrl.trim()) {
      toast.error("Please enter a GIF URL");
      return;
    }
    
    if (!gifUrl.match(/\.(gif)$/i) && !gifUrl.includes('giphy.com') && !gifUrl.includes('tenor.com')) {
      toast.error("Please enter a valid GIF URL");
      return;
    }
    
    handleSelectGif(gifUrl);
  };

  const handleGenerateGif = async () => {
    if (!gifPrompt.trim()) {
      toast.error("Please enter a prompt");
      return;
    }

    setGeneratingGif(true);
    setGeneratedGif(null);
    
    try {
      const response = await axios.post(`${API}/wallpapers/generate-gif`, {
        prompt: gifPrompt,
        style: gifStyle
      }, { timeout: 180000 }); // 3 min timeout for GIF generation

      setGeneratedGif(response.data.gif_url);
      toast.success("GIF generated!");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to generate GIF"));
    } finally {
      setGeneratingGif(false);
    }
  };

  const handleSelectGeneratedGif = () => {
    if (generatedGif) {
      handleSelectGif(generatedGif);
    }
  };

  const handleSelectCustomVideo = () => {
    if (!videoUrl.trim()) {
      toast.error("Please enter a video URL");
      return;
    }
    
    // Basic URL validation
    if (!videoUrl.match(/^https?:\/\/.+\.(mp4|webm|mov)$/i) && !videoUrl.includes('youtube.com') && !videoUrl.includes('vimeo.com')) {
      toast.error("Please enter a valid video URL (MP4, WebM, or YouTube/Vimeo link)");
      return;
    }
    
    handleSelectVideo(videoUrl);
  };

  const groupedPresets = presets.reduce((acc, preset) => {
    if (!acc[preset.category]) acc[preset.category] = [];
    acc[preset.category].push(preset);
    return acc;
  }, {});

  const groupedVideoPresets = videoPresets.reduce((acc, preset) => {
    if (!acc[preset.category]) acc[preset.category] = [];
    acc[preset.category].push(preset);
    return acc;
  }, {});

  const groupedGifPresets = GIF_PRESETS.reduce((acc, preset) => {
    if (!acc[preset.category]) acc[preset.category] = [];
    acc[preset.category].push(preset);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Image className="w-5 h-5 text-amber-500" />
            Slide Background
          </DialogTitle>
          <DialogDescription>
            Choose a preset, gradient, upload your own, or generate with AI
          </DialogDescription>
        </DialogHeader>

        {/* Apply to All Toggle */}
        {onApplyToAll && (
          <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium">Apply to all slides</span>
            </div>
            <Switch
              checked={applyToAll}
              onCheckedChange={setApplyToAll}
              data-testid="apply-to-all-switch"
            />
          </div>
        )}

        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="presets" className="gap-1 text-xs">
              <Image className="w-3 h-3" />
              Presets
            </TabsTrigger>
            <TabsTrigger value="video" className="gap-1 text-xs">
              <Video className="w-3 h-3" />
              Video
            </TabsTrigger>
            <TabsTrigger value="gif" className="gap-1 text-xs">
              <Play className="w-3 h-3" />
              GIF
            </TabsTrigger>
            <TabsTrigger value="upload" className="gap-1 text-xs">
              <Upload className="w-3 h-3" />
              Upload
            </TabsTrigger>
            <TabsTrigger value="gradients" className="gap-1 text-xs">
              <Palette className="w-3 h-3" />
              Gradients
            </TabsTrigger>
            <TabsTrigger value="ai" className="gap-1 text-xs">
              <Sparkles className="w-3 h-3" />
              AI
            </TabsTrigger>
            <TabsTrigger value="color" className="gap-1 text-xs">
              <Palette className="w-3 h-3" />
              Color
            </TabsTrigger>
          </TabsList>

          {/* Video Backgrounds */}
          <TabsContent value="video" className="mt-4">
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-6">
                {/* Custom URL input */}
                <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg space-y-3">
                  <Label className="flex items-center gap-2">
                    <Link className="w-4 h-4" />
                    Custom Video URL
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="https://example.com/video.mp4"
                      value={videoUrl}
                      onChange={(e) => setVideoUrl(e.target.value)}
                      className="flex-1"
                      data-testid="video-url-input"
                    />
                    <Button 
                      onClick={handleSelectCustomVideo}
                      className="gap-2 bg-amber-500 hover:bg-amber-600 text-black"
                      data-testid="apply-video-url-btn"
                    >
                      <Check className="w-4 h-4" />
                      Apply
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Supports MP4, WebM, or direct video links. Videos will loop and play muted.
                  </p>
                </div>

                {/* Video Presets */}
                {Object.entries(groupedVideoPresets).map(([category, items]) => (
                  <div key={category}>
                    <h4 className="text-sm font-semibold capitalize mb-3 text-muted-foreground">
                      {category}
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      {items.map((preset) => (
                        <motion.button
                          key={preset.id}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleSelectVideo(preset.url)}
                          className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all group ${
                            currentBackgroundVideo === preset.url 
                              ? 'border-amber-500 ring-2 ring-amber-500/30' 
                              : 'border-transparent hover:border-slate-300'
                          }`}
                          data-testid={`video-preset-${preset.id}`}
                        >
                          <video 
                            src={preset.url}
                            className="w-full h-full object-cover"
                            muted
                            loop
                            playsInline
                            onMouseEnter={(e) => e.target.play()}
                            onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                          <div className="absolute top-2 left-2 px-2 py-1 bg-black/50 rounded text-xs text-white flex items-center gap-1">
                            <Video className="w-3 h-3" />
                            Video
                          </div>
                          <span className="absolute bottom-2 left-2 text-xs text-white font-medium">
                            {preset.name}
                          </span>
                          {currentBackgroundVideo === preset.url && (
                            <div className="absolute top-2 right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-black" />
                            </div>
                          )}
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                            <span className="text-white text-xs font-medium">Hover to preview</span>
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                ))}

                {videoPresets.length === 0 && !loading && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Video className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No video presets available</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* GIF Backgrounds */}
          <TabsContent value="gif" className="mt-4">
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-6">
                {/* Custom GIF URL */}
                <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg space-y-3">
                  <Label className="flex items-center gap-2">
                    <Link className="w-4 h-4" />
                    Custom GIF URL
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="https://giphy.com/... or direct .gif URL"
                      value={gifUrl}
                      onChange={(e) => setGifUrl(e.target.value)}
                      className="flex-1"
                      data-testid="gif-url-input"
                    />
                    <Button 
                      onClick={handleSelectCustomGif}
                      className="gap-2 bg-amber-500 hover:bg-amber-600 text-black"
                      data-testid="apply-gif-url-btn"
                    >
                      <Check className="w-4 h-4" />
                      Apply
                    </Button>
                  </div>
                </div>

                {/* AI Generate GIF */}
                <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 rounded-lg space-y-4">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-500" />
                    <h4 className="font-semibold">AI Generated GIF</h4>
                  </div>
                  
                  <div className="space-y-3">
                    <Input
                      placeholder="Describe the animated background... (e.g., 'soft glowing cross with rays of light')"
                      value={gifPrompt}
                      onChange={(e) => setGifPrompt(e.target.value)}
                      data-testid="gif-prompt-input"
                    />
                    <Select value={gifStyle} onValueChange={setGifStyle}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {GIF_STYLE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex flex-col">
                              <span>{opt.label}</span>
                              <span className="text-xs text-muted-foreground">{opt.desc}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      onClick={handleGenerateGif}
                      disabled={generatingGif || !gifPrompt.trim()}
                      className="w-full gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                    >
                      {generatingGif ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Generating (may take 1-2 min)...
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-4 h-4" />
                          Generate Animated Background
                        </>
                      )}
                    </Button>
                  </div>

                  {generatedGif && (
                    <div className="mt-4 space-y-3">
                      <div className="aspect-video rounded-lg overflow-hidden border-2 border-purple-500">
                        <img 
                          src={generatedGif} 
                          alt="Generated GIF" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <Button 
                        onClick={handleSelectGeneratedGif}
                        className="w-full gap-2 bg-amber-500 hover:bg-amber-600 text-black"
                      >
                        <Check className="w-4 h-4" />
                        Use This Background
                      </Button>
                    </div>
                  )}
                </div>

                {/* GIF Presets */}
                {Object.entries(groupedGifPresets).map(([category, items]) => (
                  <div key={category}>
                    <h4 className="text-sm font-semibold capitalize mb-3 text-muted-foreground">
                      {category}
                    </h4>
                    <div className="grid grid-cols-3 gap-3">
                      {items.map((preset) => (
                        <motion.button
                          key={preset.id}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleSelectGif(preset.url)}
                          className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                            currentBackground === preset.url 
                              ? 'border-amber-500 ring-2 ring-amber-500/30' 
                              : 'border-transparent hover:border-slate-300'
                          }`}
                          data-testid={`gif-preset-${preset.id}`}
                        >
                          <img 
                            src={preset.url}
                            alt={preset.name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                          <div className="absolute top-2 left-2 px-2 py-1 bg-black/50 rounded text-xs text-white flex items-center gap-1">
                            <Play className="w-3 h-3" />
                            GIF
                          </div>
                          <span className="absolute bottom-2 left-2 text-xs text-white font-medium">
                            {preset.name}
                          </span>
                          {currentBackground === preset.url && (
                            <div className="absolute top-2 right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-black" />
                            </div>
                          )}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Upload Custom Image */}
          <TabsContent value="upload" className="mt-4">
            <div className="space-y-4">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleUploadImage}
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
              />
              
              <div 
                className="border-2 border-dashed rounded-xl p-12 text-center cursor-pointer hover:border-amber-500 hover:bg-amber-50/50 dark:hover:bg-amber-950/20 transition-all"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-area"
              >
                {uploading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-12 h-12 text-amber-500 animate-spin" />
                    <p className="text-sm text-muted-foreground">Uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <Upload className="w-12 h-12 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Click to upload your image</p>
                      <p className="text-sm text-muted-foreground">JPG, PNG, WebP or GIF (max 5MB)</p>
                    </div>
                  </div>
                )}
              </div>

              <p className="text-xs text-muted-foreground text-center">
                For best results, use landscape images (16:9 ratio) with dimensions of at least 1920x1080
              </p>
            </div>
          </TabsContent>

          {/* Preset Wallpapers */}
          <TabsContent value="presets" className="mt-4">
            <ScrollArea className="h-[400px] pr-4">
              {loading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(groupedPresets).map(([category, items]) => (
                    <div key={category}>
                      <h4 className="text-sm font-semibold capitalize mb-3 text-muted-foreground">
                        {category}
                      </h4>
                      <div className="grid grid-cols-3 gap-3">
                        {items.map((preset) => (
                          <motion.button
                            key={preset.id}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleSelectPreset(preset)}
                            className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                              currentBackground === preset.url 
                                ? 'border-amber-500 ring-2 ring-amber-500/30' 
                                : 'border-transparent hover:border-slate-300'
                            }`}
                            data-testid={`preset-${preset.id}`}
                          >
                            <img 
                              src={preset.url} 
                              alt={preset.name}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                            <span className="absolute bottom-2 left-2 text-xs text-white font-medium">
                              {preset.name}
                            </span>
                            {currentBackground === preset.url && (
                              <div className="absolute top-2 right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                                <Check className="w-4 h-4 text-black" />
                              </div>
                            )}
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Gradient Presets */}
          <TabsContent value="gradients" className="mt-4">
            <div className="grid grid-cols-4 gap-3">
              {GRADIENT_PRESETS.map((gradient) => (
                <motion.button
                  key={gradient.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleSelectGradient(gradient)}
                  className={`aspect-video rounded-lg overflow-hidden border-2 transition-all relative ${
                    currentBackground === gradient.value 
                      ? 'border-amber-500 ring-2 ring-amber-500/30' 
                      : 'border-transparent hover:border-slate-300'
                  }`}
                  style={{ background: gradient.value }}
                  data-testid={`gradient-${gradient.id}`}
                >
                  <span className="absolute bottom-2 left-2 text-xs text-white font-medium drop-shadow-lg">
                    {gradient.name}
                  </span>
                  {currentBackground === gradient.value && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-black" />
                    </div>
                  )}
                </motion.button>
              ))}
            </div>
          </TabsContent>

          {/* AI Generation */}
          <TabsContent value="ai" className="mt-4">
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                <p className="text-sm text-amber-800 dark:text-amber-200 flex items-center gap-2">
                  <Wand2 className="w-4 h-4" />
                  Describe the background you want and AI will create it for you
                </p>
              </div>

              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Your Prompt</Label>
                  <Input
                    placeholder="e.g., Peaceful mountains at sunrise with soft golden light"
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    data-testid="ai-prompt-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Style</Label>
                  <Select value={aiStyle} onValueChange={setAiStyle}>
                    <SelectTrigger data-testid="ai-style-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AI_STYLE_OPTIONS.map((style) => (
                        <SelectItem key={style.value} value={style.value}>
                          <div className="flex flex-col">
                            <span>{style.label}</span>
                            <span className="text-xs text-muted-foreground">{style.desc}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  onClick={handleGenerateWallpaper}
                  disabled={generating || !aiPrompt.trim()}
                  className="w-full gap-2 bg-amber-500 hover:bg-amber-600 text-black"
                  data-testid="generate-wallpaper-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating (may take up to 60s)...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generate Wallpaper
                    </>
                  )}
                </Button>
              </div>

              {/* Generated Image Preview */}
              {generatedImage && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-3"
                >
                  <Label>Generated Result</Label>
                  <div className="relative aspect-video rounded-lg overflow-hidden border">
                    <img 
                      src={generatedImage} 
                      alt="Generated wallpaper"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <Button 
                    onClick={handleSelectGenerated}
                    className="w-full gap-2"
                    data-testid="use-generated-btn"
                  >
                    <Check className="w-4 h-4" />
                    Use This Wallpaper
                  </Button>
                </motion.div>
              )}
            </div>
          </TabsContent>

          {/* Solid Color */}
          <TabsContent value="color" className="mt-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Choose Color</Label>
                <div className="flex gap-3">
                  <Input
                    type="color"
                    value={currentBackgroundType === 'color' ? currentBackground : '#000000'}
                    onChange={(e) => handleSelectColor(e.target.value)}
                    className="w-20 h-12 p-1 cursor-pointer"
                    data-testid="color-picker"
                  />
                  <Input
                    value={currentBackgroundType === 'color' ? currentBackground : '#000000'}
                    onChange={(e) => handleSelectColor(e.target.value)}
                    placeholder="#000000"
                    className="flex-1"
                  />
                </div>
              </div>

              {/* Quick color swatches */}
              <div className="space-y-2">
                <Label>Quick Colors</Label>
                <div className="flex flex-wrap gap-2">
                  {['#000000', '#1e1b4b', '#0f172a', '#1e3a8a', '#064e3b', '#4c1d95', '#881337', '#78350f'].map((color) => (
                    <button
                      key={color}
                      onClick={() => handleSelectColor(color)}
                      className={`w-10 h-10 rounded-lg border-2 transition-all ${
                        currentBackground === color && currentBackgroundType === 'color'
                          ? 'border-amber-500 ring-2 ring-amber-500/30'
                          : 'border-transparent hover:border-slate-300'
                      }`}
                      style={{ backgroundColor: color }}
                      data-testid={`color-swatch-${color.replace('#', '')}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WallpaperSelector;
